import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Plus, Send, Clock, AlertCircle, BarChart3 } from 'lucide-react';
import api from '@/lib/api';
import { formatDate } from '@/lib/utils';
import Button from '@/components/ui/Button';
import Card from '@/components/ui/Card';
import Badge from '@/components/ui/Badge';
import Pagination from '@/components/ui/Pagination';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

interface Campaign {
  id: string;
  subject: string;
  preview_text: string | null;
  status: string;
  is_ai_generated: boolean;
  total_recipients: number;
  sent_count: number;
  failed_count: number;
  scheduled_send_at: string | null;
  sent_at: string | null;
  created_at: string;
  schedule_id: string | null;
}

interface CampaignsResponse {
  campaigns: Campaign[];
  pagination: { page: number; total_pages: number; total: number; limit: number };
}

interface CampaignStats {
  sent_this_month: string;
  total_emails_sent: string;
  pending_approval: string;
  upcoming: string;
}

const statusConfig: Record<string, { variant: 'green' | 'yellow' | 'blue' | 'red' | 'gray'; label: string }> = {
  draft: { variant: 'gray', label: 'Draft' },
  pending_approval: { variant: 'yellow', label: 'Pending Approval' },
  approved: { variant: 'blue', label: 'Approved' },
  sending: { variant: 'blue', label: 'Sending...' },
  sent: { variant: 'green', label: 'Sent' },
  failed: { variant: 'red', label: 'Failed' },
  cancelled: { variant: 'gray', label: 'Cancelled' },
};

type FilterStatus = 'all' | 'draft' | 'pending_approval' | 'sent';

export default function NewsletterCampaigns() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [pagination, setPagination] = useState({ page: 1, total_pages: 1, total: 0, limit: 25 });
  const [stats, setStats] = useState<CampaignStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const currentFilter = (searchParams.get('status') || 'all') as FilterStatus;

  useEffect(() => {
    api.get<CampaignStats>('/admin/newsletter/campaigns/stats')
      .then(setStats)
      .catch(() => {});
  }, []);

  useEffect(() => {
    const fetchCampaigns = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        params.set('page', currentPage.toString());
        if (currentFilter !== 'all') params.set('status', currentFilter);

        const response = await api.get<CampaignsResponse>(
          `/admin/newsletter/campaigns?${params.toString()}`
        );
        setCampaigns(response.campaigns);
        setPagination(response.pagination);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load campaigns');
      } finally {
        setIsLoading(false);
      }
    };
    fetchCampaigns();
  }, [currentPage, currentFilter]);

  const handlePageChange = (page: number) => {
    searchParams.set('page', page.toString());
    setSearchParams(searchParams);
  };

  const handleFilterChange = (status: FilterStatus) => {
    if (status === 'all') {
      searchParams.delete('status');
    } else {
      searchParams.set('status', status);
    }
    searchParams.set('page', '1');
    setSearchParams(searchParams);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Newsletter Campaigns</h1>
          <p className="mt-2 text-sm text-gray-600">
            Create, manage, and send newsletter campaigns
          </p>
        </div>
        <Button onClick={() => navigate('/admin/newsletter/campaigns/new')}>
          <Plus className="h-4 w-4 mr-2" />
          New Campaign
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card padding="md">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Send className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Sent This Month</p>
                <p className="text-xl font-bold text-gray-900">{stats.sent_this_month}</p>
              </div>
            </div>
          </Card>
          <Card padding="md">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <BarChart3 className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Emails Sent</p>
                <p className="text-xl font-bold text-gray-900">{parseInt(stats.total_emails_sent).toLocaleString()}</p>
              </div>
            </div>
          </Card>
          <Card padding="md">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <AlertCircle className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Pending Approval</p>
                <p className="text-xl font-bold text-gray-900">{stats.pending_approval}</p>
              </div>
            </div>
          </Card>
          <Card padding="md">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Clock className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Upcoming</p>
                <p className="text-xl font-bold text-gray-900">{stats.upcoming}</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card padding="md">
        <div className="flex gap-2">
          {([
            ['all', 'All'],
            ['draft', 'Drafts'],
            ['pending_approval', 'Pending Approval'],
            ['sent', 'Sent'],
          ] as [FilterStatus, string][]).map(([value, label]) => (
            <button
              key={value}
              onClick={() => handleFilterChange(value)}
              className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                currentFilter === value
                  ? 'bg-green-100 text-green-800 font-medium'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </Card>

      {/* Table */}
      <Card padding="none">
        {error && (
          <div className="px-6 py-4 bg-red-50 border-b border-red-200 text-red-800" role="alert">
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <LoadingSpinner />
          </div>
        ) : campaigns.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            {currentFilter !== 'all' ? 'No campaigns with this status' : 'No campaigns yet. Create your first one!'}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Subject
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                      Recipients
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Scheduled
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Created
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {campaigns.map((campaign) => {
                    const config = statusConfig[campaign.status] || statusConfig.draft;
                    return (
                      <tr
                        key={campaign.id}
                        onClick={() => navigate(`/admin/newsletter/campaigns/${campaign.id}/edit`)}
                        className="hover:bg-gray-50 transition-colors cursor-pointer"
                      >
                        <td className="px-6 py-4">
                          <div>
                            <span className="text-sm font-medium text-gray-900">{campaign.subject}</span>
                            {campaign.is_ai_generated && (
                              <Badge variant="blue" className="ml-2">AI</Badge>
                            )}
                          </div>
                          {campaign.preview_text && (
                            <p className="text-xs text-gray-500 mt-1 truncate max-w-md">{campaign.preview_text}</p>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Badge variant={config.variant}>{config.label}</Badge>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                          {campaign.status === 'sent' ? (
                            <span>
                              {campaign.sent_count}
                              {campaign.failed_count > 0 && (
                                <span className="text-red-500 ml-1">({campaign.failed_count} failed)</span>
                              )}
                            </span>
                          ) : campaign.total_recipients > 0 ? (
                            campaign.total_recipients
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {campaign.scheduled_send_at ? formatDate(campaign.scheduled_send_at) : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(campaign.created_at)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {pagination.total_pages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{(pagination.page - 1) * pagination.limit + 1}</span>
                    {' '}to <span className="font-medium">{Math.min(pagination.page * pagination.limit, pagination.total)}</span>
                    {' '}of <span className="font-medium">{pagination.total}</span> results
                  </p>
                  <Pagination
                    currentPage={pagination.page}
                    totalPages={pagination.total_pages}
                    onPageChange={handlePageChange}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
